import { useHudStore } from "../store/hudStore";
import Page from "../components/Page";
import { Badge, Card } from "../design-system/components";
import "./financeDetail.css";
import { peso, EXP_CATS } from "../data/seed";

// Detail page for one cost, standing or one-off.
//
// A standing cost is an arrangement rather than an event, and the ledger could
// only ever show it as a dated chip. What you actually want to know about an
// insurance premium is what it costs a year, when the next few fall, and what it
// is charged against, none of which fits on a row.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Human wording for a recurs spec, plus how many times a year it lands. The spec
// is a machine string; nobody should have to read "every:3m:2026-08-22".
function describe(recurs: string): { label: string; perYear: number } {
  const monthly = /^monthly:(\d{1,2})$/i.exec(recurs);
  if (monthly) return { label: `Monthly, on the ${monthly[1]}${suffix(Number(monthly[1]))}`, perYear: 12 };
  const every = /^every:(\d{1,2})([dwmy]):(\d{4})-(\d{2})-(\d{2})$/i.exec(recurs);
  if (every) {
    const n = Number(every[1]);
    const unit = String(every[2]).toLowerCase();
    const word = unit === "d" ? "day" : unit === "w" ? "week" : unit === "m" ? "month" : "year";
    const per = unit === "d" ? 365 / n : unit === "w" ? 52 / n : unit === "m" ? 12 / n : 1 / n;
    const anchor = `${MONTHS[Number(every[4]) - 1]} ${Number(every[5])}`;
    return { label: n === 1 ? `Every ${word}, from ${anchor}` : `Every ${n} ${word}s, from ${anchor}`, perYear: per };
  }
  return { label: recurs || "One-off", perYear: 0 };
}
const suffix = (n: number) => (n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th");

export default function Expense() {
  const st = useHudStore();
  const standing = st.standing.find((s) => s.id === st.expenseId);
  const once = st.expenses.find((e) => e.id === st.expenseId);
  const e = standing ?? once;
  if (!e) return null;

  const isStanding = !!standing;
  const amount = e.amount;
  const cat = EXP_CATS.find((c) => c.key === (e.cat || "").toLowerCase());
  // Same fallthrough as the finance module: a subscription charged to a card is
  // funded by a `debt`, not an `account`, and "Not set" would be wrong.
  const accountName = e.account
    ? st.accounts.find((a) => a.id === e.account)?.name
      ?? st.debts.find((d) => d.id === e.account)?.name
      ?? st.debts.find((d) => d.facility === e.account)?.lender
      ?? e.account.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "Not set";
  const label = "note" in e ? e.note : "";
  const rec = isStanding ? describe(standing!.recurs) : null;
  const perYear = rec && rec.perYear > 0 ? amount * rec.perYear : 0;
  // Only this cost's own occurrences, not every standing cost's.
  const next = st.upcomingExpense.filter((u) => u.source === label).slice(0, 6);

  const stat = (label: string, value: string, accent?: string) => (
    <div className="finance-detail-metric"><span>{label}</span><strong style={{ color: accent || "var(--ink)" }}>{value}</strong></div>
  );

  return (
    <Page back="Back to Finances" onBack={st.closeExpense2}>
      <Card className="detail-stack">
        <div className="finance-detail-badges">
          <Badge tone={isStanding ? "warning" : "neutral"}><i aria-hidden="true" className={isStanding ? "fa-solid fa-rotate" : "fa-solid fa-receipt"} />{isStanding ? "Standing cost" : "One-off"}</Badge>
          {cat && <Badge style={{ color: cat.color }}><i aria-hidden="true" className={cat.fa} />{cat.label}</Badge>}
          {standing?.icon && <span className="finance-cost-icon" style={{ color: standing.color || "var(--mut)" }}><i aria-hidden="true" className={standing.icon} /></span>}
        </div>
        <h1 className="detail-heading detail-title">{label}</h1>
        <div className="finance-detail-total"><span>{isStanding ? "Each time" : "Amount"}</span><strong>{peso(amount)}</strong></div>
      </Card>
      <Card>
        <div className="finance-detail-metrics">
          {rec && stat("Repeats", rec.label)}
          {/* Annualised amounts make costs with different cadences comparable. */}
          {perYear > 0 && stat("A year", peso(perYear), "var(--orange-ink)")}
          {perYear > 0 && stat("A month", peso(perYear / 12), "var(--mut)")}
          {!isStanding && "date" in e && stat("Date", String(e.date || "Not set"))}
          {stat("Paid from", accountName)}
        </div>
      </Card>
      {next.length > 0 && <Card className="detail-stack" title="Next up" actions={<Badge>{next.length} scheduled</Badge>}>
        <div>{next.map((u, i) => (
          <div key={u.iso} className="finance-expense-occurrence" style={{ borderTop: i === 0 ? "none" : "1px solid var(--line)" }}>
            <span className="finance-detail-date">{i + 1}</span>
            <strong style={{ color: i === 0 ? "var(--accent-ink)" : "var(--ink)" }}>{u.date}{i === 0 ? " · next" : ""}</strong>
            <span className="finance-detail-money">{peso(u.amount)}</span>
          </div>
        ))}</div>
        <p className="finance-detail-note">Projected from the recurrence, not billed amounts. A standing cost is never counted as money already spent.</p>
      </Card>}
    </Page>
  );
}
